import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaPen, FaCheckCircle } from 'react-icons/fa';
import { remindersApi } from '../api/remindersApi';
import { useLanguage } from '../contexts/LanguageContext';
import shared from '../styles/reminders/ReminderCreateDialog.module.css';
import styles from '../styles/reminders/ReminderEditDialog.module.css';

const FREQUENCY_OPTIONS = [
  { value: 'once', labelKey: 'reminderOnce', fallback: 'Einmalig' },
  { value: 'daily', labelKey: 'reminderDaily', fallback: 'Täglich' },
  { value: 'weekly', labelKey: 'reminderWeekly', fallback: 'Wöchentlich' },
  { value: 'monthly', labelKey: 'reminderMonthly', fallback: 'Monatlich' },
];

function pad(n) {
  return String(n).padStart(2, '0');
}
function toDateInputValue(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function toTimeInputValue(iso) {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function formatDomain(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

/** Dialog zum Bearbeiten einer bestehenden Erinnerung (Titel, Wiederholung, Datum/Uhrzeit). */
export default function ReminderEditDialog({ reminder, onClose, onSaved }) {
  const { t } = useLanguage();
  const initialAt = reminder.snoozedUntil || reminder.nextAt;
  const [title, setTitle] = useState(reminder.title || '');
  const [frequency, setFrequency] = useState(reminder.frequency || 'once');
  const [date, setDate] = useState(toDateInputValue(initialAt));
  const [time, setTime] = useState(toTimeInputValue(initialAt));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const hasBookmark = Boolean(reminder.bookmarkUrl);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError(t('reminders.titleRequired') || 'Bitte einen Titel eingeben');
      return;
    }
    if (!date || !time) {
      setError(t('pleaseSelectDateTime') || 'Bitte Datum und Uhrzeit auswählen');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const nextAt = new Date(`${date}T${time}`).toISOString();
      // Eine manuell geänderte Zeit hebt ein eventuell laufendes "Snooze" auf
      await remindersApi.update(reminder.id, {
        title: title.trim(),
        frequency,
        nextAt,
        snoozedUntil: null,
      });
      setSuccess(true);
      setTimeout(() => {
        onSaved && onSaved();
      }, 700);
    } catch (err) {
      setError(err.message || t('reminders.saveError') || 'Fehler beim Speichern.');
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className={shared.overlay} onClick={onClose}>
      <div className={shared.dialog} onClick={(e) => e.stopPropagation()}>
        <header className={shared.header}>
          <div className={shared.headerLeft}>
            <FaPen className={shared.headerIcon} />
            <h2 className={shared.headerTitle}>
              {t('reminders.editTitle') || 'Erinnerung bearbeiten'}
            </h2>
          </div>
          <button
            className={shared.closeBtn}
            onClick={onClose}
            aria-label={t('reminders.close') || 'Schließen'}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </header>

        {hasBookmark && (
          <div className={shared.bookmarkInfo}>
            <span className={styles.bookmarkTitle}>
              🔖 {reminder.bookmarkTitle || 'Lesezeichen'}
            </span>
            <span className={shared.bookmarkUrl}>{formatDomain(reminder.bookmarkUrl)}</span>
          </div>
        )}

        {error && (
          <div className={shared.errorBanner} role="alert">
            {error}
          </div>
        )}

        {success && (
          <div className={shared.successBanner} role="status">
            <FaCheckCircle />
            <span>{t('reminders.saved') || 'Erinnerung aktualisiert.'}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className={shared.section}>
            <label className={shared.sectionLabel} htmlFor="reminder-edit-title">
              {t('reminders.titleLabel') || 'Titel'}
            </label>
            <input
              id="reminder-edit-title"
              type="text"
              className={styles.titleInput}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={loading}
              maxLength={120}
              required
            />
          </div>

          <div className={shared.section}>
            <label className={shared.sectionLabel}>{t('frequency') || 'Wiederholung'}</label>
            <div className={shared.frequencyGrid}>
              {FREQUENCY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`${shared.frequencyBtn} ${frequency === opt.value ? shared.active : ''}`}
                  onClick={() => setFrequency(opt.value)}
                  disabled={loading}
                >
                  {t(opt.labelKey) || opt.fallback}
                </button>
              ))}
            </div>
          </div>

          <div className={shared.section}>
            <label className={shared.sectionLabel}>{t('customDateTime') || 'Datum & Uhrzeit'}</label>
            <div className={shared.customRow}>
              <input
                type="date"
                className={shared.dateInput}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                disabled={loading}
              />
              <input
                type="time"
                className={shared.timeInput}
                value={time}
                onChange={(e) => setTime(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className={styles.footer}>
            <button type="submit" className={shared.submitBtn} disabled={loading}>
              {loading ? (t('reminders.saving') || 'Speichert...') : (t('reminders.save') || 'Speichern')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
